import React, {useEffect} from 'react'
import {Box, Text} from '@chakra-ui/react'
import {useContentAsset} from '../../hooks/useScapiCustomApi'
import CopyOnly from './copy-only'
import PropTypes from 'prop-types'
import LoadingSpinner from '../../components/loading-spinner'
import TeaserList from './teaser-list'
import Hero from './hero'
import ProductAnimated from './product-animated'
import Awards from './awards'
import ModuleErrorBoundary from './module-error-boundary'
import Carousel from './carousel'
import Accordion from './accordion'
import Gallery from './gallery'
import Cards from './cards'
import HighlightStack from './highlight-stacks'
import ProductColors from './product-colors'
import Overlay from './overlay'
import {trackModuleView} from '../../tracking/analytics'
import Campaign from './campaign'
import useIntersectionObserver from '../../hooks/use-intersection-observer'
import Collage from './collage'
import Media from './media'
import ComparisonTable from './comparison-table'
import ProductCarousel from './product-carousel'
import TextBanner from './text-banner'
import ContentTeaser from './content-teaser'
import PaymentMethods from './payment-methods'
import UgcCarousel from './ugc-carousel'
import MessageBar from './message-bar'
import Newsletter from './newsletter'
import Header from './preview-navigation/header'
import NavigationLevel1 from './preview-navigation/level1'
import NavigationLevel2 from './preview-navigation/level2'
import {ModulePreviewProvider, withPreviewOnly} from './preview-context'

const MODULES_MAP = {
    'leap-copy-only': CopyOnly,
    'leap-teaser-list': TeaserList,
    'leap-header': withPreviewOnly(Header),
    'leap-hero': Hero,
    'leap-navigation-level1': withPreviewOnly(NavigationLevel1),
    'leap-navigation-level2': withPreviewOnly(NavigationLevel2),
    'leap-product-animated': ProductAnimated,
    'leap-product-colors': ProductColors,
    'leap-awards': Awards,
    'leap-carousel': Carousel,
    'leap-accordion': Accordion,
    'leap-gallery': Gallery,
    'leap-cards': Cards,
    'leap-highlights-stack': HighlightStack,
    'leap-overlay': Overlay,
    'leap-campaign': Campaign,
    'leap-collage': Collage,
    'leap-media': Media,
    'leap-comparison-table': ComparisonTable,
    'leap-product-carousel': ProductCarousel,
    'leap-text-banner': TextBanner,
    'leap-content-teaser': ContentTeaser,
    'leap-payment-methods': PaymentMethods,
    'leap-ugc-carousel': UgcCarousel,
    'leap-message-bar': MessageBar,
    'leap-newsletter': Newsletter
}

const ModuleRenderer = ({moduleProps, moduleType, assetId, index}) => {
    const Module = MODULES_MAP[moduleType]

    const hasTrackedView = React.useRef(false)

    const trackingOptions = {
        useOnce: true,
        initialInView: false
    }

    const [setRef, wasDisplayed] = useIntersectionObserver(trackingOptions)

    useEffect(() => {
        if (wasDisplayed && !hasTrackedView.current) {
            trackModuleView({
                moduleName: moduleType,
                moduleProps
            })

            hasTrackedView.current = true
        }
    }, [wasDisplayed, moduleType, moduleProps])

    if (!Module) {
        return (
            <Box key={index} border="1px solid red" p={4} m={4}>
                <Text color="red">
                    <span>Unsupported module type&nbsp;</span>
                    <strong>{moduleType}</strong>
                    <span>&nbsp;from content asset</span>
                    <strong>{assetId}</strong>
                    <span>&nbsp;position&nbsp;</span>
                    <strong>{index + 1}</strong>
                </Text>
                <Text as="pre" bg="red">
                    {JSON.stringify(moduleProps, null, 2)}
                </Text>
            </Box>
        )
    }

    return <Module {...moduleProps} moduleType={moduleType} ref={setRef} />
}

ModuleRenderer.propTypes = {
    moduleProps: PropTypes.object,
    moduleType: PropTypes.string.isRequired,
    assetId: PropTypes.string.isRequired,
    index: PropTypes.number.isRequired
}

const CmsModules = ({
    assetId,
    modules: preloadedModules,
    hideInConfiguratorSidebar = false,
    preview = false
}) => {
    const {data: loadedModules, isInitialLoading} = useContentAsset(
        {parameters: {assetId}},
        {enabled: !preloadedModules && Boolean(assetId)}
    )

    // preloadedModules may change at any time so select dynamically
    const modules = preloadedModules || loadedModules

    const visibleModules = React.useMemo(() => {
        if (!Array.isArray(modules)) {
            return modules
        }

        return hideInConfiguratorSidebar
            ? modules.filter(({hideInConfiguratorSidebar: hideModule}) => hideModule !== true)
            : modules
    }, [hideInConfiguratorSidebar, modules])

    if (isInitialLoading) {
        return <LoadingSpinner />
    }

    if (typeof modules?.map !== 'function') {
        return null
    }

    return (
        <ModulePreviewProvider preview={preview}>
            {visibleModules.map(({moduleType, errorMessage, ...module}, index) => (
                <ModuleErrorBoundary
                    key={index}
                    moduleType={moduleType}
                    assetId={assetId}
                    position={index + 1}
                    moduleConfig={module}
                >
                    {errorMessage && (
                        <Box key={index} border="1px solid red" p={4} m={4}>
                            <Text color="red">
                                <span>{errorMessage}&nbsp;</span>
                                <span>inside module type&nbsp;</span>
                                <strong>{moduleType}&nbsp;</strong>
                                <span>from content asset&nbsp;</span>
                                <strong>{assetId}</strong>
                            </Text>
                            <Text as="pre" bg="red">
                                {JSON.stringify(module, null, 2)}
                            </Text>
                        </Box>
                    )}

                    <ModuleRenderer
                        moduleProps={module}
                        moduleType={moduleType}
                        assetId={assetId}
                        index={index}
                    />
                </ModuleErrorBoundary>
            ))}
        </ModulePreviewProvider>
    )
}

CmsModules.propTypes = {
    assetId: PropTypes.string,
    modules: PropTypes.array,
    hideInConfiguratorSidebar: PropTypes.bool,
    preview: PropTypes.bool
}

export default CmsModules
